/**
 * Admin-managed Good Lending finance calculator settings.
 *
 * Stored as a singleton Firestore document at `settings/financeCalculator`.
 * Any seller whose `financeSource === 'good-lending'` will have this config
 * rendered on their listings (subject to the per-listing min-price + saleType
 * gates checked at render time on ListingPage).
 */
export interface FinanceCalculatorSettings {
  /** Master kill-switch — when false, no Good Lending calc renders anywhere. */
  enabled: boolean

  /** Provider branding */
  providerName: string
  providerLogo?: string  // Firebase Storage URL
  providerUrl?: string
  description?: string   // 1-3 sentence intro shown above the calculator

  /** Contact details surfaced to buyers under the calc */
  contactEmail?: string
  contactPhone?: string

  /** Calculator behaviour */
  defaultRate: number     // % p.a. — e.g. 9.95
  minAmount: number       // listings under this price won't show the calc
  maxTermYears: number    // clamped 1..7 at render time

  /** Optional terms link */
  termsUrl?: string

  /** Per-tier access policy. Absent → resolver falls back to DEFAULT_TIER_POLICIES. */
  tierPolicies?: Record<FinancePolicyTier, FinanceTierPolicy>
}

/** Default values used when the singleton doc doesn't exist yet. */
export const DEFAULT_FINANCE_SETTINGS: FinanceCalculatorSettings = {
  enabled: false,
  providerName: 'Good Lending',
  defaultRate: 9.95,
  minAmount: 1200,
  maxTermYears: 5,
}

/** How sellers in a given tier opt into the Good Lending calculator. */
export type FinanceOptInMode = 'off' | 'per-listing' | 'settings' | 'always'

/** Per-tier Good Lending access policy. */
export interface FinanceTierPolicy {
  mode: FinanceOptInMode
  /** Allowed category-group ids, or 'all' for no category restriction. */
  allowedGroups: string[] | 'all'
}

export type FinancePolicyTier = 'general' | 'storefront' | 'dealership' | 'nonprofit'

/** Seeded defaults — "pump the brakes" on general; tweakable in /admin/finance. */
export const DEFAULT_TIER_POLICIES: Record<FinancePolicyTier, FinanceTierPolicy> = {
  general:    { mode: 'per-listing', allowedGroups: ['automotive'] },
  storefront: { mode: 'settings',    allowedGroups: 'all' },
  dealership: { mode: 'settings',    allowedGroups: 'all' },
  nonprofit:  { mode: 'off',         allowedGroups: [] },
}
